import { useState, useEffect, useMemo } from 'react';
import { productUsageService } from '../../services/api';

const useProductUsageData = ({ isAuthenticated, isAdmin }) => {
  // State for API data
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [countries, setCountries] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25
  });

  // Applied filters (used for API calls)
  const [serverFilters, setServerFilters] = useState({
    fromDate: '',
    toDate: '',
    country: '',
    search: '',
    profile: ''
  });

  // Form input values (separate from applied filters)
  const [filterInputs, setFilterInputs] = useState({
    fromDate: '',
    toDate: '',
    country: '',
    search: '',
    profile: ''
  });

  // Calculate page count
  const pageCount = useMemo(() => {
    return Math.ceil(totalCount / pagination.pageSize) || 0;
  }, [totalCount, pagination.pageSize]);

  // Helper function to format Date object to DD/MM/YYYY format for API
  const formatDateForAPI = (date) => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Helper function to parse date string into Date object
  const parseDateString = (dateString) => {
    if (!dateString) return null;
    try {
      // Parse DD/MM/YYYY format
      const parts = dateString.split('/');
      if (parts.length === 3) {
        return new Date(parts[2], parts[1] - 1, parts[0]); // Year, Month-1, Day
      }
      return null;
    } catch (err) {
      return null;
    }
  };

  // Get default date range (last 30 days)
  const getDefaultDateRange = () => {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 30);
    return {
      fromDate: formatDateForAPI(fromDate),
      toDate: formatDateForAPI(toDate)
    };
  };

  // Initialize with default date range
  useEffect(() => {
    const defaults = getDefaultDateRange();
    setFilterInputs(prev => ({
      ...prev,
      fromDate: defaults.fromDate,
      toDate: defaults.toDate
    }));
    setServerFilters(prev => ({
      ...prev,
      fromDate: defaults.fromDate,
      toDate: defaults.toDate
    }));
  }, []);

  // Fetch data with server-side parameters
  const fetchProductUsageData = async () => {
    if (!isAuthenticated) {
      setProducts([]);
      setTotalCount(0);
      setCountries([]);
      setProfiles([]);
      setLoading(false);
      return;
    }

    // Don't fetch if dates are not set yet
    if (!serverFilters.fromDate || !serverFilters.toDate) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        ...serverFilters
      };

      const data = await productUsageService.getProductUsageData(params);

      if (data) {
        setProducts(data.products || []);
        setTotalCount(data.totalCount || 0);
        setCountries(data.countries || []);
        // Profiles only returned for admin users
        if (isAdmin && data.profiles) {
          setProfiles(data.profiles || []);
        }
      } else {
        setProducts([]);
        setTotalCount(0);
        setCountries([]);
        setProfiles([]);
      }
    } catch (err) {
      console.error('Error fetching product usage data:', err);

      // Extract error message from API response
      let errorMessage = 'Failed to load product usage data';

      if (err.response?.data) {
        const responseData = err.response.data;

        if (responseData.status === 'error' && responseData.message) {
          errorMessage = responseData.message;
        } else if (responseData.message) {
          errorMessage = responseData.message;
        } else if (typeof responseData === 'string') {
          errorMessage = responseData;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setProducts([]);
      setTotalCount(0);
      setCountries([]);
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when dependencies change
  useEffect(() => {
    fetchProductUsageData();
  }, [isAuthenticated, isAdmin, pagination.pageIndex, pagination.pageSize, serverFilters]);

  // Handle input changes (doesn't trigger API call)
  const handleInputChange = (field, value) => {
    setFilterInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Apply filters (triggers API call)
  const applyFilters = () => {
    setServerFilters(filterInputs);
    // Reset to first page when filters change
    setPagination(prev => ({
      ...prev,
      pageIndex: 0
    }));
  };

  // Clear all filters (reset to defaults)
  const clearAllFilters = () => {
    const defaults = getDefaultDateRange();
    const emptyFilters = {
      fromDate: defaults.fromDate,
      toDate: defaults.toDate,
      country: '',
      search: '',
      profile: ''
    };

    setServerFilters(emptyFilters);
    setFilterInputs(emptyFilters);
    // Reset to first page when filters are cleared
    setPagination(prev => ({
      ...prev,
      pageIndex: 0
    }));
  };

  return {
    // Data
    products,
    totalCount,
    pageCount,
    countries,
    profiles,
    loading,
    error,

    // Pagination
    pagination,
    setPagination,

    // Filters
    serverFilters,
    filterInputs,
    handleInputChange,
    applyFilters,
    clearAllFilters,

    // Date helpers
    parseDateString,
    formatDateForAPI,
    getDefaultDateRange
  };
};

export default useProductUsageData;
